import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { workflowRunsHitlApi } from '@/api/workflowRunsHitl'
import { useTenantKey } from '@/hooks/useTenantKey'
import type { PendingUserInputRequest } from '@/hooks/useChatRunStream'

interface Question {
  id: string
  prompt: string
  kind: 'text' | 'number' | 'textarea'
  options?: string[]
}

interface PerQuestionDraft {
  selectedOption: string | null
  freeText: string
  source: 'suggested' | 'freetext' | null
}

interface DraftState {
  currentIndex: number
  perQuestion: Record<string, PerQuestionDraft>
}

const DRAFT_KEY_PREFIX = 'idsp.nodeUserInputDraft.'

function makeEmptyDraft(questions: Question[]): DraftState {
  const perQuestion: Record<string, PerQuestionDraft> = {}
  for (const q of questions) {
    perQuestion[q.id] = { selectedOption: null, freeText: '', source: null }
  }
  return { currentIndex: 0, perQuestion }
}

function loadDraft(requestId: string, questions: Question[]): DraftState {
  try {
    const raw = localStorage.getItem(DRAFT_KEY_PREFIX + requestId)
    if (!raw) return makeEmptyDraft(questions)
    const parsed = JSON.parse(raw) as Partial<DraftState>
    const empty = makeEmptyDraft(questions)
    return {
      currentIndex: typeof parsed.currentIndex === 'number' ? parsed.currentIndex : 0,
      perQuestion: { ...empty.perQuestion, ...(parsed.perQuestion ?? {}) },
    }
  } catch {
    return makeEmptyDraft(questions)
  }
}

function saveDraft(requestId: string, draft: DraftState) {
  try {
    localStorage.setItem(DRAFT_KEY_PREFIX + requestId, JSON.stringify(draft))
  } catch {
    // Quota exceeded etc. — silently drop
  }
}

function clearDraft(requestId: string) {
  try {
    localStorage.removeItem(DRAFT_KEY_PREFIX + requestId)
  } catch {
  }
}

function parseQuestions(json: string): Question[] {
  try {
    const arr = JSON.parse(json)
    if (!Array.isArray(arr)) return []
    return arr
      .filter((q) => q && typeof q.id === 'string' && typeof q.prompt === 'string')
      .map((q) => ({
        id: q.id,
        prompt: q.prompt,
        kind: q.kind === 'number' || q.kind === 'textarea' ? q.kind : 'text',
        options: Array.isArray(q.options) ? q.options : undefined,
      }))
  } catch {
    return []
  }
}

interface Props {
  projectId: string
  pending: PendingUserInputRequest
  onSubmitted: () => void
}

export function NodeQuestionPaginator({ projectId, pending, onSubmitted }: Props) {
  const questions = useMemo(() => parseQuestions(pending.questionsJson), [pending.questionsJson])
  const [draft, setDraft] = useState<DraftState>(() => loadDraft(pending.requestId, questions))
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  const freeTextInputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null)

  useEffect(() => {
    saveDraft(pending.requestId, draft)
  }, [pending.requestId, draft])

  useEffect(() => {
    setDraft(loadDraft(pending.requestId, questions))
  }, [pending.requestId, questions])

  const total = questions.length
  const currentQuestion = questions[draft.currentIndex]
  const isLast = draft.currentIndex === total - 1

  const submitMutation = useMutation({
    mutationFn: () => {
      const notesQuestion = questions.find((q) => q.id.toLowerCase() === 'notes')
      const answers = questions
        .filter((q) => q !== notesQuestion)
        .map((q) => {
          const d = draft.perQuestion[q.id]
          if (!d || d.source === null) return null
          const value = d.source === 'suggested' ? d.selectedOption ?? '' : d.freeText
          if (!value) return null
          return { questionId: q.id, value }
        })
        .filter((a): a is { questionId: string; value: string } => a !== null)

      const notesDraft = notesQuestion ? draft.perQuestion[notesQuestion.id] : null
      const notes = notesDraft?.source === 'freetext' ? notesDraft.freeText : notesDraft?.selectedOption ?? undefined

      return workflowRunsHitlApi.submitUserInput(projectId, pending.runId, pending.nodeId, {
        requestId: pending.requestId,
        answers,
        notes,
      })
    },
    onSuccess: () => {
      clearDraft(pending.requestId)
      queryClient.invalidateQueries({
        queryKey: tenantKey('projects', projectId, 'chats'),
      })
      onSubmitted()
    },
    onError: () => {
      toast.error("Couldn't submit your answers — try again.")
    },
  })

  if (!currentQuestion) {
    return null
  }

  const setQuestion = (qid: string, patch: Partial<PerQuestionDraft>) => {
    setDraft((prev) => ({
      ...prev,
      perQuestion: { ...prev.perQuestion, [qid]: { ...prev.perQuestion[qid], ...patch } },
    }))
  }

  const goTo = (idx: number) => {
    setDraft((prev) => ({ ...prev, currentIndex: Math.max(0, Math.min(total - 1, idx)) }))
  }

  const onSubmit = () => submitMutation.mutate()

  const draftFor = draft.perQuestion[currentQuestion.id] ?? { selectedOption: null, freeText: '', source: null }

  return (
    <div className="border-t bg-gradient-to-b from-info to-background px-6 py-4">
      <div className="mx-auto w-full max-w-3xl">
        <div className="mb-2 flex items-center justify-between text-xs uppercase tracking-wide">
          <span className="inline-flex items-center gap-2">
            <span className="rounded-full bg-info-foreground px-2 py-0.5 text-[10px] normal-case text-background">
              Node · {pending.nodeName}
            </span>
            <span className="text-info-foreground">asking</span>
          </span>
          <span className="text-muted-foreground">
            Question {draft.currentIndex + 1} of {total}
          </span>
        </div>

        <div className="rounded-lg border bg-card p-4 shadow-sm">
          <p className="mb-3 text-sm font-semibold">{currentQuestion.prompt}</p>

          {currentQuestion.options && currentQuestion.options.length > 0 && (
            <div className="mb-2 flex flex-col gap-1.5">
              {currentQuestion.options.map((opt) => {
                const selected = draftFor.source === 'suggested' && draftFor.selectedOption === opt
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() =>
                      setQuestion(currentQuestion.id, { selectedOption: opt, source: 'suggested' })
                    }
                    className={[
                      'flex items-center gap-3 rounded-md border px-3 py-2 text-left text-sm transition',
                      selected
                        ? 'border-info-foreground bg-info font-semibold text-foreground'
                        : 'border-border bg-background hover:border-info-foreground/60 hover:bg-info/50',
                    ].join(' ')}
                  >
                    <span
                      className={[
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                        selected ? 'border-info-foreground bg-info-foreground' : 'border-border',
                      ].join(' ')}
                    >
                      {selected && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                    </span>
                    <span className="flex-1">{opt}</span>
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      suggested
                    </span>
                  </button>
                )
              })}
            </div>
          )}

          <div
            className={[
              'flex items-center gap-2 rounded-md border bg-background',
              draftFor.source === 'freetext' ? 'border-info-foreground ring-2 ring-info' : 'border-border',
            ].join(' ')}
          >
            <button
              type="button"
              aria-label="Select free text answer"
              aria-pressed={draftFor.source === 'freetext'}
              onClick={() => {
                setQuestion(currentQuestion.id, { source: 'freetext' })
                freeTextInputRef.current?.focus()
              }}
              className={[
                'ml-3 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                draftFor.source === 'freetext' ? 'border-info-foreground bg-info-foreground' : 'border-border',
              ].join(' ')}
            >
              {draftFor.source === 'freetext' && (
                <span className="h-1.5 w-1.5 rounded-full bg-white" />
              )}
            </button>
            {currentQuestion.kind === 'textarea' ? (
              <textarea
                ref={freeTextInputRef as React.RefObject<HTMLTextAreaElement>}
                value={draftFor.freeText}
                onChange={(e) => {
                  const v = e.target.value
                  setQuestion(currentQuestion.id, {
                    freeText: v,
                    source: v ? 'freetext' : draftFor.source === 'freetext' ? null : draftFor.source,
                  })
                }}
                onFocus={() => {
                  if (draftFor.freeText && draftFor.source !== 'freetext') {
                    setQuestion(currentQuestion.id, { source: 'freetext' })
                  }
                }}
                placeholder="Or type your own answer…"
                className="min-h-[3rem] flex-1 resize-y bg-transparent px-2 py-2 text-sm focus:outline-none"
              />
            ) : (
              <input
                ref={freeTextInputRef as React.RefObject<HTMLInputElement>}
                type={currentQuestion.kind === 'number' ? 'number' : 'text'}
                value={draftFor.freeText}
                onChange={(e) => {
                  const v = e.target.value
                  setQuestion(currentQuestion.id, {
                    freeText: v,
                    source: v ? 'freetext' : draftFor.source === 'freetext' ? null : draftFor.source,
                  })
                }}
                onFocus={() => {
                  if (draftFor.freeText && draftFor.source !== 'freetext') {
                    setQuestion(currentQuestion.id, { source: 'freetext' })
                  }
                }}
                placeholder="Or type your own answer…"
                className="flex-1 bg-transparent px-2 py-2 text-sm focus:outline-none"
              />
            )}
            <span className="mr-3 text-[10px] uppercase tracking-wide text-muted-foreground">
              free text
            </span>
          </div>

          <div className="mt-3 flex items-center justify-between border-t pt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={draft.currentIndex === 0}
              onClick={() => goTo(draft.currentIndex - 1)}
            >
              ← Back
            </Button>

            <div className="flex gap-1.5">
              {questions.map((q, i) => {
                const answered =
                  !!draft.perQuestion[q.id] && draft.perQuestion[q.id].source !== null
                const current = i === draft.currentIndex
                return (
                  <button
                    key={q.id}
                    type="button"
                    title={`Question ${i + 1}`}
                    onClick={() => goTo(i)}
                    className={[
                      'h-2 w-2 rounded-full transition',
                      current
                        ? 'scale-125 bg-info-foreground ring-2 ring-info'
                        : answered
                        ? 'bg-green-500'
                        : 'bg-muted-foreground/30',
                    ].join(' ')}
                  />
                )
              })}
            </div>

            {isLast ? (
              <Button
                type="button"
                size="sm"
                disabled={submitMutation.isPending}
                onClick={onSubmit}
              >
                {submitMutation.isPending ? 'Submitting…' : 'Submit & resume →'}
              </Button>
            ) : (
              <Button type="button" size="sm" onClick={() => goTo(draft.currentIndex + 1)}>
                Next →
              </Button>
            )}
          </div>
        </div>

        <div className="mt-2 text-xs text-muted-foreground">
          Partial answers are fine —{' '}
          <button type="button" onClick={onSubmit} className="underline hover:text-info-foreground">
            submit now without finishing
          </button>{' '}
          if you prefer.
        </div>
      </div>
    </div>
  )
}
